import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import usuarioService, { Usuario } from '../services/usuarioService';

interface UsuarioSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (usuario: Usuario) => void;
}

const UsuarioSearch: React.FC<UsuarioSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState<boolean>(false);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (verTodos) setVerTodos(false);
            const trimmed = searchTerm.trim();
            
            if (trimmed !== '') {
                try {
                    const usuario = await usuarioService.getUsuario(trimmed);
                    setUsuarios(usuario ? [usuario] : []);
                    if (!usuario) setError('No se encontraron usuarios.');
                } catch (err: any) {
                    const items = await usuarioService.getUsuarios({
                        searchTerm: trimmed,
                        pageNumber: 1,
                        pageSize: 50
                    });
                    setUsuarios(items || []);
                    if (!items || items.length === 0) {
                        setError('No se encontraron usuarios.');
                    }
                } finally {
                    setLoading(false);
                }
                return;
            }

            const items = await usuarioService.getUsuarios({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize: 50
            });
            setUsuarios(items || []);
            if (!items || items.length === 0) {
                setError('No se encontraron usuarios.');
            }
        } catch (err: any) {
            setError(`Error al buscar usuarios: ${err.message || 'Error desconocido'}`);
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, verTodos]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            const items = await usuarioService.getUsuarios({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize: 1000
            });

            setUsuarios(items || []);
            if (!items || items.length === 0) {
                setError('No se encontraron usuarios.');
            }
        } catch (err: any) {
            setError(`Error al cargar usuarios: ${err.message || 'Error desconocido'}`);
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (usuario: Usuario) => {
        onSelect(usuario);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setUsuarios([]);
        setError(null);
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setUsuarios([]);
            setError(null);
            return;
        }
        const handle = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Usuario
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Nombre de usuario o email"
                        inputProps={{ 'aria-label': 'Buscar usuario' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleSearch}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button 
                            variant={verTodos ? 'contained' : 'text'} 
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {usuarios.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {usuarios.map((usuario) => (
                                        <Card 
                                            key={usuario.usuario} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(usuario)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        {usuario.usuario}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(usuario);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {usuario.nombre || '-'}
                                                        </Typography>
                                                    </Box>
                                                    {usuario.email && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Email
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {usuario.email}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Usuario</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {usuarios.map((usuario) => (
                                            <TableRow 
                                                key={usuario.usuario} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(usuario)}
                                            >
                                                <TableCell>{usuario.usuario}</TableCell>
                                                <TableCell>{usuario.nombre || '-'}</TableCell>
                                                <TableCell>{usuario.email || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(usuario);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && usuarios.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron usuarios
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UsuarioSearch;
